import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { endpointsMetadata } from "../../../common/infrastructure/routes/endpointsMetadata";
import { IViewStatisticsViewModel } from "../models/viewStatisticsViewModel";
import { StatisticsTypeLabels } from "../../../common/labels";
import { StatisticsType } from "../../../common/enums";
import { useApiQuery } from "../../../common/infrastructure/hooks/useApiQuery";
import { dateUtility } from "../../../common/infrastructure/utilities/dateUtility";
import { exportStatisticsToExcel } from "../utilities/statisticsExcelExportUtility";
import {
  ALL_HALLS_URL_TOKEN,
  ALL_USERS_URL_TOKEN,
} from "../../../common/infrastructure/routes/routePaths";
import { ISelectFieldModel } from "../../../common/components/forms/inputs/dropdown/models/selectFieldModel";
import { useAppContext } from "../../../AppContext";

export const useViewStatisticsPage = () => {
  const { type, dateFrom, dateTo, userIds, hallIds } = useParams();
  const { selectedStudio } = useAppContext();
  const tenantUsesMoreThanOneHall =
    selectedStudio?.tenantUsesMoreThanOneHall ?? false;

  const isAllHalls =
    !hallIds || hallIds === ALL_HALLS_URL_TOKEN || hallIds.trim() === "";
  const hallIdsForApi = isAllHalls ? ALL_HALLS_URL_TOKEN : hallIds;

  const isAllUsers =
    !userIds || userIds === ALL_USERS_URL_TOKEN || userIds.trim() === "";
  const userIdsForApi = isAllUsers ? ALL_USERS_URL_TOKEN : userIds;

  const { data: availableHalls } = useApiQuery<ISelectFieldModel[]>({
    queryKey: [endpointsMetadata.studioHallTypeNomenclature.getAll],
    endpoint: endpointsMetadata.studioHallTypeNomenclature.getAll,
    enabled: tenantUsesMoreThanOneHall && !isAllHalls,
    globalIsLoading: false,
  });

  const selectedHallNames = useMemo(() => {
    if (isAllHalls || !hallIds) return [];
    const ids = hallIds
      .split(",")
      .map(Number)
      .filter((n) => !isNaN(n) && n > 0);
    if (ids.length === 0) return [];
    if (!availableHalls) return ids.map((id) => `#${id}`);
    return ids.map(
      (id) => availableHalls.find((h) => h.id === id)?.name ?? `#${id}`,
    );
  }, [isAllHalls, hallIds, availableHalls]);

  const getSubtitle = () => {
    const statisticType = StatisticsTypeLabels[(type ?? 100) as StatisticsType];
    const dateFromParsed = dateUtility.formatDate(dateFrom);
    const dateToParsed = dateUtility.formatDate(dateTo);
    return `${statisticType}: ${dateFromParsed} - ${dateToParsed}`;
  };

  const subtitle = getSubtitle();
  const { data: result, isLoading } = useApiQuery<IViewStatisticsViewModel>(
    {
      queryKey: [
        endpointsMetadata.statistics.generateStatistics,
        type,
        dateFrom,
        dateTo,
        userIdsForApi,
        hallIdsForApi,
      ],
      endpoint: `${endpointsMetadata.statistics.generateStatistics}?type=${type}&dateFrom=${dateFrom}&dateTo=${dateTo}&userIds=${userIdsForApi}&hallIds=${hallIdsForApi}`,
    },
    { staleTime: 5 * 60 * 1000 }
  );

  const handleExportExcel = () => {
    if (!result) return;
    exportStatisticsToExcel(
      result,
      dateFrom ?? "",
      dateTo ?? "",
      selectedHallNames,
    );
  };

  return {
    subtitle,
    result,
    isLoading,
    handleExportExcel,
    selectedHallNames,
    isAllHalls,
  };
};
